import * as React from 'react'
import styled from '@theme/styled-components'
import Theme from '@theme/Theme'
import Loader from 'react-loader-spinner'
import { BsDownload } from 'react-icons/bs'
import { RiAddFill } from 'react-icons/ri'

interface Props {
  title: string
  onClick: () => void
  type: ButtonType
  status: ButtonStatus
  size?: ButtonSize
  fitContent?: boolean
  icon?: 'bin' | 'cancel' | 'download' | 'add'
}

const Button = ({ title, onClick, type, status, size = 'medium', fitContent, icon }: Props) => {
  return (
    <Container
      onClick={status === 'active' ? onClick : undefined}
      type={type}
      status={status}
      size={size}
      fitContent={fitContent}>
      {status !== 'loading' ? (
        <>
          {!!icon && (
            <IconContainer>
              {icon === 'download' ? (
                <BsDownload
                  size={30}
                  color={
                    type === 'classic' && status !== 'disabled'
                      ? Theme.colors.classicButtonLoader
                      : Theme.colors.buttonLoader
                  }
                />
              ) : icon === 'bin' ? (
                <svg
                  width="18"
                  height="20"
                  viewBox="0 0 18 20"
                  fill={
                    type === 'classic' && status !== 'disabled'
                      ? Theme.colors.classicButtonLoader
                      : Theme.colors.buttonLoader
                  }>
                  <path d="M11 16C11.553 16 12 15.553 12 15V9C12 8.448 11.553 8 11 8C10.447 8 10 8.448 10 9V15C10 15.553 10.447 16 11 16ZM14 17C14 17.553 13.553 18 13 18H5C4.448 18 4 17.553 4 17V6H14V17ZM7 3C7 2.448 7.448 2 8 2H10C10.553 2 11 2.448 11 3V4H7V3ZM17 4H13V3C13 1.343 11.657 0 10 0H8C6.343 0 5 1.343 5 3V4H1C0.448 4 0 4.448 0 5C0 5.552 0.448 6 1 6H2V17C2 18.657 3.343 20 5 20H13C14.657 20 16 18.657 16 17V6H17C17.553 6 18 5.552 18 5C18 4.448 17.553 4 17 4ZM7 16C7.552 16 8 15.553 8 15V9C8 8.448 7.552 8 7 8C6.448 8 6 8.448 6 9V15C6 15.553 6.448 16 7 16Z" />
                </svg>
              ) : icon === 'add' ? (
                <RiAddFill
                  size={20}
                  color={
                    type === 'classic' && status !== 'disabled'
                      ? Theme.colors.classicButtonLoader
                      : Theme.colors.buttonLoader
                  }
                />
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill={
                    type === 'classic' && status !== 'disabled'
                      ? Theme.colors.classicButtonLoader
                      : Theme.colors.buttonLoader
                  }>
                  <path d="M11.4099 10.0002L17.7099 3.71019C18.1021 3.31806 18.1021 2.68231 17.7099 2.29019C17.3178 1.89806 16.6821 1.89806 16.2899 2.29019L9.99994 8.59019L3.70994 2.29019C3.31782 1.89806 2.68206 1.89806 2.28994 2.29019C1.89782 2.68231 1.89782 3.31806 2.28994 3.71019L8.58994 10.0002L2.28994 16.2902C2.10063 16.478 1.99414 16.7335 1.99414 17.0002C1.99414 17.2668 2.10063 17.5224 2.28994 17.7102C2.47771 17.8995 2.7333 18.006 2.99994 18.006C3.26658 18.006 3.52217 17.8995 3.70994 17.7102L9.99994 11.4102L16.2899 17.7102C16.4777 17.8995 16.7333 18.006 16.9999 18.006C17.2666 18.006 17.5222 17.8995 17.7099 17.7102C17.8993 17.5224 18.0057 17.2668 18.0057 17.0002C18.0057 16.7335 17.8993 16.478 17.7099 16.2902L11.4099 10.0002Z" />
                </svg>
              )}
            </IconContainer>
          )}
          <Title type={type} status={status} size={size} className="button_title">
            {title}
          </Title>
        </>
      ) : (
        <Loader
          type="ThreeDots"
          color={type === 'classic' ? Theme.colors.classicButtonLoader : Theme.colors.buttonLoader}
          height={size === 'big' ? 30 : size === 'medium' ? 15 : 10}
        />
      )}
    </Container>
  )
}

export default Button

const Container = styled('div')<{ type: ButtonType; status: ButtonStatus; size: ButtonSize; fitContent?: boolean }>`
  background: ${(props) =>
    props.status === 'disabled'
      ? props.theme.colors.disabledButton
      : props.type === 'action'
      ? `linear-gradient(270deg, ${props.theme.colors.actionButton01} 0%, ${props.theme.colors.actionButton02} 100%)`
      : props.type === 'navigation'
      ? `linear-gradient(270deg, ${props.theme.colors.navigationButton01} 0%, ${props.theme.colors.navigationButton02} 100%)`
      : props.theme.colors.classicButton};
  height: ${(props) => (props.size === 'big' ? '75px' : props.size === 'medium' ? '50px' : '25px')};
  width: fit-content;
  ${(props) => !props.fitContent && 'min-width: 200px;'}
  padding: 0px 25px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50px;

  ${(props) =>
    props.type === 'classic' &&
    props.status !== 'disabled' &&
    `border: 1px solid ${props.theme.colors.classicButtonBorder};`}
  ${(props) =>
    props.type === 'action' &&
    props.status !== 'disabled' &&
    `box-shadow: 0px 10px 30px ${props.theme.colors.actionButtonshadow};`}
  ${(props) =>
    props.type === 'navigation' &&
    props.status !== 'disabled' &&
    `box-shadow: 0px 10px 30px ${props.theme.colors.navigationButtonShadow};`}

  cursor: ${(props) => (props.status === 'disabled' ? 'not-allowed' : 'pointer')};

  &:hover {
    background: ${(props) =>
      props.type === 'action'
        ? `linear-gradient(270deg, ${props.theme.colors.actionButton02} 0%, ${props.theme.colors.actionButton01} 100%)`
        : props.type === 'navigation'
        ? `linear-gradient(270deg, ${props.theme.colors.navigationButton02} 0%, ${props.theme.colors.navigationButton01} 100%)`
        : `linear-gradient(270deg, ${props.theme.colors.navigationButton01} 0%, ${props.theme.colors.navigationButton02} 100%)`};
    .button_title {
      color: white;
    }
  }
`
const Title = styled('div')<{ type: ButtonType; status: ButtonStatus; size: ButtonSize }>`
  ${(props) =>
    props.size === 'big'
      ? props.theme.fonts.buttonBig
      : props.size === 'medium'
      ? props.theme.fonts.buttonMedium
      : props.theme.fonts.buttonSmall};
  ${(props) => props.type === 'classic' && props.status !== 'disabled' && `color: ${props.theme.colors.primary_text};`}
  margin: 0;
`
const IconContainer = styled('div')`
  margin-right: 12px;
`
